// client/src/pages/IssuesPage.jsx
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Plus, X, ChevronRight, Wrench, Building2, Loader2, CheckCircle2, ArrowRight, Camera } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useIssues, useAddIssue, useAddIssuePhoto, useVehicles, useRaiseJobRequest } from '../api/hooks'
import CalendarPicker from '../components/ui/CalendarPicker'

// ─── Constants ────────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  low:      { dot: 'bg-blue-400',   badge: 'bg-blue-500/15 text-blue-400'     },
  medium:   { dot: 'bg-amber-400',  badge: 'bg-amber-500/15 text-amber-400'   },
  high:     { dot: 'bg-orange-400', badge: 'bg-orange-500/15 text-orange-400' },
  critical: { dot: 'bg-red-400',    badge: 'bg-red-500/15 text-red-400'       },
}

const STATUS_STYLES = {
  open:        'bg-blue-500/15 text-blue-400',
  quoted:      'bg-purple-500/15 text-purple-400',
  approved:    'bg-indigo-500/15 text-indigo-400',
  in_progress: 'bg-amber-500/15 text-amber-400',
  resolved:    'bg-green-500/15 text-green-400',
  deferred:    'bg-gray-500/15 text-gray-400',
}

const REPAIR_INDICATOR = {
  requested:         { label: 'Request sent',       colour: 'text-blue-400'   },
  quoted:            { label: 'Quote ready',         colour: 'text-purple-400' },
  awaiting_approval: { label: 'Awaiting your approval', colour: 'text-yellow-400' },
  approved:          { label: 'Approved',            colour: 'text-indigo-400' },
  in_progress:       { label: 'Repair in progress', colour: 'text-amber-400'  },
  waiting_on_parts:  { label: 'Waiting on parts',   colour: 'text-orange-400' },
  completed:         { label: 'Repair complete',    colour: 'text-green-400'  },
}

const STATUS_LABELS = {
  open: 'Open', quoted: 'Quoted', approved: 'Approved',
  in_progress: 'In Progress', resolved: 'Resolved', deferred: 'Deferred',
}

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

// ─── Plate ────────────────────────────────────────────────────────────────────

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-2 py-0.5 rounded">
      {registration}
    </span>
  )
}

// ─── Raise Job modal (customer only) ─────────────────────────────────────────

function RaiseJobModal({ issue, allIssues, onClose }) {
  const raiseJob = useRaiseJobRequest()

  // Other open/deferred issues for the same vehicle
  const related = allIssues.filter(i =>
    i.id !== issue.id &&
    i.vehicle_id === issue.vehicle_id &&
    ['open', 'deferred'].includes(i.status)
  )

  const [extraIds,      setExtraIds]      = useState([])
  const [message,       setMessage]       = useState('')
  const [preferredDate, setPreferredDate] = useState('')

  const allSelected = [issue.id, ...extraIds]

  const toggle = (id) =>
    setExtraIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const derivedTitle = allSelected.length === 1
    ? `Fix: ${issue.title}`
    : `Fix: ${allSelected.length} issues — ${issue.title}`

  const handleSubmit = async () => {
    try {
      await raiseJob.mutateAsync({
        vehicleId:    issue.vehicle_id,
        title:        derivedTitle,
        description:  message.trim() || null,
        issueIds:     allSelected,
        preferredDate: preferredDate || null,
      })
      toast.success('Job request sent to your garage')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit request')
    }
  }

  const hasGarage = !!issue.primary_garage_id

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Request Repair</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Primary issue */}
          <div>
            <p className="text-xs font-medium text-[var(--text-muted)] mb-2">Reporting issue</p>
            <div className="flex items-center gap-2 px-4 py-3 bg-[var(--bg-base)] border border-indigo-500/30 rounded-xl">
              {issue.registration && <Plate registration={issue.registration} />}
              <span className="text-sm font-medium text-[var(--text-primary)]">{issue.title}</span>
            </div>
          </div>

          {/* Additional issues for same vehicle */}
          {related.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-muted)] mb-2">
                Also include from same vehicle
              </p>
              <div className="space-y-1.5">
                {related.map(i => (
                  <label
                    key={i.id}
                    className="flex items-center gap-3 px-4 py-2.5 bg-[var(--bg-base)]
                               border border-[var(--border)] rounded-xl cursor-pointer
                               hover:bg-[var(--bg-overlay)] transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={extraIds.includes(i.id)}
                      onChange={() => toggle(i.id)}
                      className="accent-indigo-500 shrink-0"
                    />
                    <span className="text-sm text-[var(--text-primary)]">{i.title}</span>
                    <span className={clsx('ml-auto shrink-0 px-1.5 py-0.5 rounded text-xs font-medium',
                      SEVERITY_STYLES[i.severity]?.badge || 'bg-gray-500/15 text-gray-400')}>
                      {i.severity || 'low'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Garage */}
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl border
                          border-[var(--border)] bg-[var(--bg-base)]">
            <Building2 size={14} className={hasGarage ? 'text-indigo-400' : 'text-[var(--text-muted)]'} />
            {hasGarage ? (
              <div>
                <p className="text-xs text-[var(--text-muted)]">Sending to</p>
                <p className="text-sm font-medium text-[var(--text-primary)]">{issue.primary_garage_name}</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-amber-400">No garage registered</p>
                <p className="text-xs text-[var(--text-muted)]">
                  Go to Settings → My Garage to register your vehicle with a garage first.
                </p>
              </div>
            )}
          </div>

          {/* Preferred date */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5">
              Preferred repair date (optional)
            </label>
            <CalendarPicker
              value={preferredDate}
              onChange={setPreferredDate}
              minDate={new Date()}
              placeholder="Pick a preferred date…"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5">
              Message to garage (optional)
            </label>
            <textarea
              rows={2}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Any additional details for the garage…"
              className={clsx(inputCls, 'resize-none')}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!hasGarage || raiseJob.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40
                         text-white text-sm font-medium rounded-lg transition-colors
                         flex items-center justify-center gap-2"
            >
              {raiseJob.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Sending…</>
                : <><Wrench size={14} /> Submit Request</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Report Issue modal ───────────────────────────────────────────────────────

function ReportIssueModal({ onClose, onRequestRepair }) {
  const { data: vehicles = [] } = useVehicles()
  const addIssue   = useAddIssue()
  const addPhoto   = useAddIssuePhoto()
  const mediaRef   = useRef(null)
  const [form, setForm] = useState({
    vehicleId: '', title: '', description: '', severity: 'medium',
    dateIdentified: new Date().toISOString().slice(0, 10),
  })
  const [pendingFiles, setPendingFiles] = useState([])
  const [created, setCreated]           = useState(null)
  const [attachedCount, setAttachedCount] = useState(0)
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleMediaPick = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length) setPendingFiles(prev => [...prev, ...files])
    e.target.value = ''
  }

  const removeFile = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx))

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const issue = await addIssue.mutateAsync({
        vehicleId:      form.vehicleId,
        title:          form.title,
        description:    form.description || null,
        severity:       form.severity    || null,
        dateIdentified: form.dateIdentified || null,
      })

      // Upload any selected media files
      let ok = 0
      for (const file of pendingFiles) {
        try {
          await addPhoto.mutateAsync({ issueId: issue.id, file })
          ok++
        } catch (err) {
          toast.error(err.response?.data?.error || `Failed to upload ${file.name}`)
        }
      }
      if (ok > 0) setAttachedCount(ok)

      const vehicle = vehicles.find(v => String(v.id) === String(form.vehicleId))
      setCreated({ ...issue, ...vehicle && {
        registration:        vehicle.registration,
        make:                vehicle.make,
        model:               vehicle.model,
        primary_garage_id:   vehicle.primary_garage_id,
        primary_garage_name: vehicle.primary_garage_name,
      }})
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to report issue')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">
            {created ? 'Issue Reported' : 'Report Issue'}
          </h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        {created ? (
          /* ── Next-step screen ── */
          <div className="p-6 space-y-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={22} className="text-green-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[var(--text-primary)]">{created.title}</p>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">Saved as an open issue.</p>
              </div>
            </div>

            <div className="px-4 py-4 bg-indigo-500/5 border border-indigo-500/20 rounded-xl space-y-1">
              <p className="text-sm font-semibold text-[var(--text-primary)]">What happens next?</p>
              <p className="text-sm text-[var(--text-secondary)]">
                To get this fixed you need to request a repair from your garage. They will review
                the issue, raise a job, and send you a quote before any work begins.
              </p>
            </div>

            {attachedCount > 0 && (
              <p className="text-xs text-green-400 text-center">
                {attachedCount} photo{attachedCount > 1 ? 's/videos' : '/video'} attached.
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                           text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors"
              >
                Done
              </button>
              <button
                onClick={() => { onClose(); onRequestRepair(created) }}
                disabled={!created.primary_garage_id}
                title={!created.primary_garage_id ? 'No garage linked to this vehicle' : undefined}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-indigo-600
                           hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-medium
                           rounded-lg transition-colors"
              >
                <Wrench size={14} />
                Request Repair
                <ArrowRight size={14} />
              </button>
            </div>
            {!created.primary_garage_id && (
              <p className="text-xs text-[var(--text-muted)] text-center -mt-2">
                No garage linked — go to Settings → My Garage to register first.
              </p>
            )}
          </div>
        ) : (
          /* ── Form ── */
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="flex items-start gap-2.5 px-3 py-3 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg">
              <Wrench size={14} className="text-indigo-400 shrink-0 mt-0.5" />
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                Reporting an issue saves it for your records. To get it fixed, you'll need to
                request a repair from your garage — they'll then raise a job and send you a quote.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Vehicle</label>
              <select value={form.vehicleId} onChange={set('vehicleId')} className={inputCls} required>
                <option value="">Select a vehicle…</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.registration} — {v.make} {v.model}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Title</label>
              <input type="text" value={form.title} onChange={set('title')}
                placeholder="e.g. Strange noise from engine" className={inputCls} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Description</label>
              <textarea rows={3} value={form.description} onChange={set('description')}
                placeholder="Describe the issue in more detail…"
                className={clsx(inputCls, 'resize-none')} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Severity</label>
                <select value={form.severity} onChange={set('severity')} className={inputCls}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Date identified</label>
                <input type="date" value={form.dateIdentified} onChange={set('dateIdentified')}
                  className={inputCls} />
              </div>
            </div>
            {/* Media picker */}
            <div className="border border-[var(--border)] rounded-xl">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-medium text-[var(--text-primary)]">
                  Photos &amp; Videos
                  {pendingFiles.length > 0 && (
                    <span className="ml-1.5 text-xs text-indigo-400 font-normal">
                      {pendingFiles.length} selected
                    </span>
                  )}
                </span>
                <button type="button" onClick={() => mediaRef.current?.click()}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium
                             bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                             text-[var(--text-secondary)] hover:text-[var(--text-primary)]
                             rounded-lg transition-colors">
                  <Camera size={12} />
                  Add
                </button>
                <input ref={mediaRef} type="file" accept="image/*,video/*"
                  multiple className="hidden" onChange={handleMediaPick} />
              </div>
              {pendingFiles.length > 0 ? (
                <div className="px-4 pb-3 space-y-1">
                  {pendingFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                      <span className="flex-1 truncate">{f.name}</span>
                      <button type="button" onClick={() => removeFile(i)}
                        className="text-[var(--text-muted)] hover:text-red-400 transition-colors">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-4 pb-3 text-xs text-[var(--text-muted)]">
                  Optional — helps your garage understand the problem.
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                           text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={addIssue.isPending || addPhoto.isPending}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                           text-white text-sm font-medium rounded-lg transition-colors">
                {addIssue.isPending || addPhoto.isPending ? 'Reporting…' : 'Report Issue'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// ─── Issue row ────────────────────────────────────────────────────────────────

function IssueRow({ issue, isCustomer, onRaiseJob }) {
  const navigate    = useNavigate()
  const severity    = issue.severity || 'low'
  const dotStyle    = SEVERITY_STYLES[severity]?.dot  || 'bg-gray-400'
  const statusStyle = STATUS_STYLES[issue.status]     || 'bg-gray-500/15 text-gray-400'

  const canRaise = isCustomer &&
    ['open', 'deferred'].includes(issue.status) &&
    issue.primary_garage_id

  return (
    <div className="flex items-center gap-2 bg-[var(--bg-surface)] border border-[var(--border)]
                    rounded-xl hover:border-[var(--border-strong)] transition-all group">
      <button
        onClick={() => navigate(`/issues/${issue.id}`)}
        className="flex items-center gap-4 px-5 py-4 flex-1 min-w-0 text-left"
      >
        <div className={clsx('w-2.5 h-2.5 rounded-full shrink-0', dotStyle)} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            {issue.registration && <Plate registration={issue.registration} />}
            <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors truncate">
              {issue.title}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs text-[var(--text-muted)]">
              {issue.make && `${issue.make} ${issue.model} · `}
              {new Date(issue.date_identified).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </p>
            {isCustomer && issue.linked_job_status && REPAIR_INDICATOR[issue.linked_job_status] && (
              <span className={clsx('text-xs font-medium', REPAIR_INDICATOR[issue.linked_job_status].colour)}>
                · {REPAIR_INDICATOR[issue.linked_job_status].label}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', statusStyle)}>
            {STATUS_LABELS[issue.status] || issue.status}
          </span>
          <ChevronRight size={15} className="text-[var(--text-muted)] group-hover:text-indigo-400" />
        </div>
      </button>

      {canRaise && (
        <button
          onClick={(e) => { e.stopPropagation(); onRaiseJob(issue) }}
          title="Request garage fix"
          className="shrink-0 mr-3 p-2 text-[var(--text-muted)] hover:text-indigo-400
                     hover:bg-indigo-500/10 rounded-lg transition-colors"
        >
          <Wrench size={15} />
        </button>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { v: '',            l: 'All'         },
  { v: 'open',        l: 'Open'        },
  { v: 'in_progress', l: 'In Progress' },
  { v: 'resolved',    l: 'Resolved'    },
]

export default function IssuesPage() {
  const { user } = useAuth()
  const [statusFilter, setStatusFilter] = useState('')
  const [showReport,   setShowReport]   = useState(false)
  const [raiseIssue,   setRaiseIssue]   = useState(null)

  const isCustomer = user?.role === 'customer'

  const { data: issues = [], isLoading } = useIssues(
    statusFilter ? { status: statusFilter } : {}
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Issues</h1>
          {!isLoading && issues.length > 0 && (
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {issues.length} {issues.length === 1 ? 'issue' : 'issues'}
            </p>
          )}
        </div>
        {isCustomer && (
          <button
            onClick={() => setShowReport(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={16} />
            Report Issue
          </button>
        )}
      </div>

      {/* Status filter */}
      <div className="flex gap-1.5 flex-wrap">
        {STATUS_FILTERS.map(f => (
          <button key={f.v} onClick={() => setStatusFilter(f.v)}
            className={clsx('px-3 py-1 rounded-full text-xs font-medium transition-colors',
              statusFilter === f.v
                ? 'bg-indigo-600 text-white'
                : 'bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            )}>
            {f.l}
          </button>
        ))}
      </div>

      {isCustomer && issues.some(i => ['open', 'deferred'].includes(i.status) && i.primary_garage_id) && (
        <p className="text-xs text-[var(--text-muted)]">
          Tap the <Wrench size={11} className="inline" /> icon on any open issue to request a repair from your garage.
        </p>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : issues.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center mb-4">
            <AlertTriangle size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">No issues</h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-xs">
            {statusFilter
              ? 'No issues match this filter.'
              : isCustomer
                ? 'Report a fault or concern with one of your vehicles.'
                : 'No reported issues for your garage\'s vehicles.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {issues.map(i => (
            <IssueRow
              key={i.id}
              issue={i}
              isCustomer={isCustomer}
              onRaiseJob={setRaiseIssue}
            />
          ))}
        </div>
      )}

      {showReport  && (
        <ReportIssueModal
          onClose={() => setShowReport(false)}
          onRequestRepair={(issue) => { setShowReport(false); setRaiseIssue(issue) }}
        />
      )}}
      {raiseIssue  && (
        <RaiseJobModal
          issue={raiseIssue}
          allIssues={issues}
          onClose={() => setRaiseIssue(null)}
        />
      )}
    </div>
  )
}
